/**
 * Days into the columns a time chart draws (CR-33).
 *
 * The server answers one row per clinic day, zeros included. Thirty columns read as a month; three
 * hundred and sixty do not — they turn into a barcode — so a long range is folded into weeks or
 * months here, from the same rows, rather than asking the server three different questions.
 *
 * Dates stay `YYYY-MM-DD` strings throughout and are read in UTC, never in the browser's zone: a
 * calendar date has no time in it, and `new Date('2026-10-11')` formatted west of Greenwich is the
 * tenth (the trap `format.shortDate` documents).
 */

export type Granularity = 'day' | 'week' | 'month';

export interface DayCounts {
  readonly date?: string;
  readonly total?: number;
  readonly open?: number;
  readonly completed?: number;
  readonly noShow?: number;
  readonly cancelled?: number;
}

export interface Bucket {
  /** The first day in it. */
  readonly start: string;
  /** The last day in it, included. */
  readonly end: string;
  readonly total: number;
  readonly open: number;
  readonly completed: number;
  readonly noShow: number;
  readonly cancelled: number;
}

/** Up to about six weeks a column a day; up to about six months a column a week; then months. */
export function granularityFor(dayCount: number): Granularity {
  if (dayCount <= 45) {
    return 'day';
  }
  return dayCount <= 190 ? 'week' : 'month';
}

/** The key of the column a day falls in: the day itself, its week's Saturday, or its month. */
export function bucketKey(isoDate: string, granularity: Granularity): string {
  if (granularity === 'day') {
    return isoDate;
  }
  if (granularity === 'month') {
    return `${isoDate.slice(0, 7)}-01`;
  }
  // The Egyptian working week starts on Saturday (ASM-004), so a week column runs Saturday to
  // Friday and a Friday off ends one week rather than splitting it.
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  const sinceSaturday = (date.getUTCDay() + 1) % 7;
  date.setUTCDate(date.getUTCDate() - sinceSaturday);
  return date.toISOString().slice(0, 10);
}

export function bucketDays(days: readonly DayCounts[], granularity: Granularity): Bucket[] {
  const buckets = new Map<string, { -readonly [K in keyof Bucket]: Bucket[K] }>();
  for (const day of days) {
    if (!day.date) {
      continue;
    }
    const key = bucketKey(day.date, granularity);
    const bucket = buckets.get(key) ?? {
      start: day.date,
      end: day.date,
      total: 0,
      open: 0,
      completed: 0,
      noShow: 0,
      cancelled: 0,
    };
    if (day.date < bucket.start) {
      bucket.start = day.date;
    }
    if (day.date > bucket.end) {
      bucket.end = day.date;
    }
    bucket.total += day.total ?? 0;
    bucket.open += day.open ?? 0;
    bucket.completed += day.completed ?? 0;
    bucket.noShow += day.noShow ?? 0;
    bucket.cancelled += day.cancelled ?? 0;
    buckets.set(key, bucket);
  }
  return [...buckets.values()].sort((a, b) => a.start.localeCompare(b.start));
}

/** Calendar days from `from` to `to`, both included. */
export function daysBetween(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return 0;
  }
  return Math.round((end - start) / 86_400_000) + 1;
}

/** `part` of `whole` as a whole-number percentage; nothing of nothing is 0, not NaN. */
export function percent(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/**
 * The top of a chart's scale: the largest value rounded up to a figure a person would write on an
 * axis (4, 6, 10, 20, 50, 100…), and even, so the half-way gridline is a whole number too.
 */
export function niceCeiling(value: number): number {
  if (value <= 2) {
    return 2;
  }
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 4, 5, 6, 8, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value && candidate % 2 === 0) {
      return candidate;
    }
  }
  return 10 * magnitude;
}

export interface ValueBucket {
  readonly start: string;
  readonly end: string;
  /** The days' values added together, position by position. */
  readonly values: number[];
}

/**
 * Any per-day series folded into the chart's columns (CR-34): the same folding as
 * {@link bucketDays}, for rows that carry other figures — money sold, taken and refunded, and the
 * number of sales — given as one array per day.
 */
export function bucketValues(
  days: readonly { readonly date: string; readonly values: readonly number[] }[],
  granularity: Granularity,
): ValueBucket[] {
  const buckets = new Map<string, { start: string; end: string; values: number[] }>();
  for (const day of days) {
    const key = bucketKey(day.date, granularity);
    const bucket = buckets.get(key) ?? { start: day.date, end: day.date, values: day.values.map(() => 0) };
    if (day.date < bucket.start) {
      bucket.start = day.date;
    }
    if (day.date > bucket.end) {
      bucket.end = day.date;
    }
    day.values.forEach((value, index) => {
      bucket.values[index] = (bucket.values[index] ?? 0) + value;
    });
    buckets.set(key, bucket);
  }
  return [...buckets.values()].sort((a, b) => a.start.localeCompare(b.start));
}
